import { createContext, useContext, useEffect, useState } from "react";
import { api, setToken, getToken, refreshSession, apiError } from "@/lib/api";
import { setCacheUser, clearOfflineData } from "@/lib/offlineCache";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading

  useEffect(() => {
    let alive = true;
    // Démarrage : renouvellement silencieux via le cookie de session (persistant 30 j glissants), puis profil.
    // Aucune redirection vers /login tant que cette vérification n'est pas terminée (user === undefined → loader).
    const bootstrap = async () => {
      try {
        const token = getToken();
        if (!token) {
          const data = await refreshSession();
          if (alive) setUser(data.user);
          return;
        }
        const { data } = await api.get("/auth/me");
        if (alive) setUser(data.user);
      } catch (e) {
        if (!alive) return;
        if (e.response?.status === 401 || e.response?.status === 403) { setToken(null); setUser(null); }
        else {
          // Réseau indisponible (hors ligne) : on conserve la session locale si un jeton existe encore.
          const t = getToken();
          setUser(t ? (() => { try { return JSON.parse(localStorage.getItem("sanitdocs_user") || "null"); } catch (_) { return null; } })() : null);
        }
      }
    };
    bootstrap();
    const onExpired = () => { setToken(null); clearOfflineData(); setUser(null); };
    window.addEventListener("sanitdocs:session-expired", onExpired);
    // Retour au premier plan (PWA) : on rafraîchit le jeton si nécessaire.
    const onVisible = () => { if (document.visibilityState === "visible" && getToken()) api.get("/auth/me").then((r) => setUser(r.data.user)).catch(() => {}); };
    document.addEventListener("visibilitychange", onVisible);
    // Rôle modifié par un Admin : la navigation suit dès le prochain rafraîchissement périodique (le serveur applique déjà les nouveaux droits).
    const tick = setInterval(() => { if (getToken() && document.visibilityState === "visible") api.get("/auth/me").then((r) => setUser((u) => (u && u.role !== r.data.user.role ? r.data.user : u))).catch(() => {}); }, 60000);
    return () => { alive = false; clearInterval(tick); window.removeEventListener("sanitdocs:session-expired", onExpired); document.removeEventListener("visibilitychange", onVisible); };
  }, []);

  useEffect(() => {
    if (user === undefined) return; // chargement : ne pas effacer la session locale (nécessaire au démarrage hors connexion)
    if (user) setCacheUser(user.id); else clearOfflineData();
    try { user ? localStorage.setItem("sanitdocs_user", JSON.stringify(user)) : localStorage.removeItem("sanitdocs_user"); } catch (_) {}
  }, [user]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setToken(data.access_token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: apiError(e.response?.data?.detail) || e.message };
    }
  };

  const setSession = (data) => { setToken(data.access_token); setUser(data.user); };

  const demoLogin = async (profile) => {
    try {
      const sandbox_token = localStorage.getItem("sanitdocs_demo_sandbox") || undefined;
      const { data } = await api.post("/auth/demo", { profile, sandbox_token });
      localStorage.setItem("sanitdocs_demo_sandbox", data.sandbox_token);
      localStorage.setItem("sanitdocs_demo_expires", data.demo_expires_at);
      setToken(data.access_token);
      setUser(data.user);
      localStorage.setItem("sanitdocs_demo_tour", profile);
      window.dispatchEvent(new Event("sanitdocs-demo"));
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: apiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_) {}
    setToken(null);
    clearOfflineData();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, demoLogin, logout, setSession }}>
      {children}
    </AuthContext.Provider>
  );
}
